/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { Button, Form, FormProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { FormGrid, SaveButtonContainer, SequenceForm } from './Sequences.styles';
import {
  SequenceChangesType,
  sequenceTypeBySlug,
  SequenceTypesIds,
  TravelPlan,
  TravelSequence,
} from 'types';
import { FormFieldsSequence } from './FormFieldsSequence';
import { SelectType } from './SelectType';
import {
  getAirline,
  getChaperonePhone,
  getGroundTransportationCompany,
  getTransporterPhone,
  onSaveTravelSequence,
  populateFormOnLoad,
} from './FormSequence.utilities';
import { useDetailsState } from '../useDetailsState';

interface Props extends FormProps {
  onChangeSequenceType: (e: SequenceTypesIds) => void;
  sequence: TravelSequence;
  index: number;
  fetchPackage: () => void;
}

export function FormSequence({
  onChangeSequenceType,
  sequence,
  index,
  fetchPackage,
}: Props) {
  const { state } = useDetailsState();
  const travelPlan = state?.travelPlan as TravelPlan;
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState<boolean[]>([]);
  const [sequenceType, setSequenceType] = useState(
    sequenceTypeBySlug[
      sequence.travelSequenceTypeSlug ?? SequenceTypesIds.FlightSequence
    ],
  );
  const itemSequenceType = sequenceTypeBySlug[sequence.travelSequenceTypeSlug ?? ''];

  const onFinish = async (e: Store) => {
    const cloneIsSubmitting = [...isSubmitting];
    cloneIsSubmitting[index] = true;
    setIsSubmitting(cloneIsSubmitting);
    await onSaveTravelSequence(e, sequenceType, index, travelPlan, sequence);
    sequence.isNew = false;
    setIsSubmitting([]);
    fetchPackage();
  };

  const changeSequenceType = (e: SequenceTypesIds) => {
    setSequenceType(e);
    onChangeSequenceType(e);
  };

  const setValueChaperonePhone = (e: number): void => {
    form.setFieldsValue({
      [`chaperonePhone${index}`]: getChaperonePhone(e),
    });
  };

  const setValueGroundTransportationPhoneNotes = (e: number): void => {
    const groundCompanyInfo = getGroundTransportationCompany(e);
    form.setFieldsValue({
      [`companyPhone${index}`]: groundCompanyInfo?.phone,
      [`companyNotes${index}`]: groundCompanyInfo?.additionalInformation,
    });
  };

  const setValueAirlinePhoneNotes = (e: number): void => {
    const airline = getAirline(e);
    form.setFieldsValue({
      [`airlinePhone${index}`]: airline?.phone,
      [`airlineNotes${index}`]: airline?.additionalInformation,
    });
  };

  const setValueTransporterPhone = (e: number): void => {
    form.setFieldsValue({
      [`TransporterPhone${index}`]: getTransporterPhone(e),
    });
  };

  const handleFormChanges = (e: number, type: SequenceChangesType): void => {
    switch (type) {
      case 'GroundTransportation': {
        setValueGroundTransportationPhoneNotes(e);
        break;
      }
      case 'Airline': {
        setValueAirlinePhoneNotes(e);
        break;
      }
      case 'Chaperone': {
        setValueChaperonePhone(e);
        break;
      }
      case 'Transporter':
      default: {
        setValueTransporterPhone(e);
      }
    }
  };

  return (
    <SequenceForm
      size="large"
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      initialValues={
        sequence.travelSequenceId
          ? populateFormOnLoad(sequence, itemSequenceType, index)
          : undefined
      }
    >
      <FormGrid>
        <Form.Item label="Type">
          <SelectType
            defaultValue={itemSequenceType}
            onChange={changeSequenceType}
            disabled={!sequence.isNew}
          />
        </Form.Item>
        {itemSequenceType && (
          <FormFieldsSequence
            sequenceType={itemSequenceType}
            index={index}
            onChanges={(e: number, type: SequenceChangesType) =>
              handleFormChanges(e, type)
            }
          />
        )}
      </FormGrid>
      {itemSequenceType && (
        <SaveButtonContainer>
          <Button type="primary" loading={isSubmitting[index]} ghost htmlType="submit">
            Save Changes
          </Button>
        </SaveButtonContainer>
      )}
    </SequenceForm>
  );
}
